import {db} from '@/firebase'
import EventEmitter from 'events'
import { store } from '@/store'
import i18n from '@/i18n'
import helpers from '../mixins/helpers'

import doctorRepository from './DoctorRepository'
import officeRepository from './OfficeRepository'
import patientRepository from './PatientRepository'

import moment from 'moment'

// Extend EventEmitter so user of EmergencyRepository can react to our own defined events. (ex: emergencyRepository.on('added'))
class TimeSlotRepository extends EventEmitter {
    constructor () {
        super()

        this.collection = db.collection('timeSlots')

        return this
    }

    subscribe () {
        this.attachFirebaseListeners()
    }

    unsubscribe() {
        console.log('unsubscribed DoctorOfficeRepository')

        this.detachFirebaseListeners()
    }

    create (doctor, office, date, time, onSuccess, onError) {
        this.collection
            .where('doctorId', '==', doctor.id)
            .where('officeId', '==', office.id)
            .where('date', '==', date)
            .where('time', '==', time)
            .get()
            .then((querySnapshot) => {
                if (! querySnapshot.empty) {
                    onError({
                        message: "You already created this time slot.",
                        query: {
                            doctor: doctor.id,
                            office: office.id,
                            date: date,
                            time: time
                        }
                    })
                    return false
                }

                var newDoc = this.collection.doc()
                var id = newDoc.id

                var timeSlot = {
                    id, date, time,
                    type: doctor.type,
                    appointmentId: null,
                    appointmentInvitationId: null,
                    doctorId: doctor.id,
                    doctorFirstName: doctor.firstName,
                    doctorLastName: doctor.lastName,
                    doctorAvatar: doctor.avatar,
                    isDoctorRegulated: doctor.isRegulated,
                    officeId: office.id,
                    officeName: office.name,
                    officeStreetName: office.streetName,
                    officeStreetNumber: office.streetNumber,
                    officeZipCode: office.zipCode,
                    officeLocality: office.locality,
                    officeCountryId: office.countryId,
                    officeCountry: i18n.t('Countries.' + store.state.countries[office.countryId].code) || 'Belgique',
                    officeLocation: office.location,
                    officePhone: office.phone
                }

                this.collection.doc(id)
                    .set(timeSlot)
                    .then(() => {
                        onSuccess(timeSlot)
                    })
                    .catch(onError);
            })
            .catch((error) => {
                onError(error)
            })
    }

    delete (doctor, office, date, time, onSuccess, onError) {
        this.collection
            .where('doctorId', '==', doctor.id)
            .where('officeId', '==', office.id)
            .where('date', '==', date)
            .where('time', '==', time)
            .get()
            .then((querySnapshot) => {
                if (querySnapshot.empty) {
                    onError({message: "This time slot is already deactivated."})
                    return false
                }

                querySnapshot.docs[0].ref.delete().then(onSuccess, onError).catch(onError)
            })
            .catch(onError);
    }

    allForDate (doctorId, officeId, date, onSuccess, onError) {
        this.collection
            .where('doctorId', '==', doctorId)
            .where('officeId', '==', officeId)
            .where('date', '==', date)
            .get()
            .then((querySnapshot) => {
                let timeSlots = {}

                querySnapshot.forEach((doc) => {
                    timeSlots[doc.id] = doc.data()
                });

                onSuccess(timeSlots)
            })
            .catch((error) => {
                onError(error)
            })
    }

    allAvailable (doctorId, officeId, date, onSuccess, onError) {
        this.collection
            .where('doctorId', '==', doctorId)
            .where('officeId', '==', officeId)
            .where('date', '==', date)
            .get()
            .then((querySnapshot) => {
                let timeSlots = {}

                querySnapshot.forEach((doc) => {
                    timeSlots[doc.id] = doc.data()
                });

                onSuccess(timeSlots)
            })
            .catch((error) => {
                onError(error)
            })
    }

    allFutureAvailable (doctorId, officeId, onSuccess, onError) {
        this.collection
            .where('doctorId', '==', doctorId)
            .where('officeId', '==', officeId)
            .where('appointmentId', '==', null)
            .where('appointmentInvitationId', '==', null)
            .where('date', '>=', moment().format('YYYY-MM-DD')) // TODO add an index
            .where('date', '<=', moment().add(4, 'days').format('YYYY-MM-DD'))
            .get()
            .then((querySnapshot) => {
                let timeSlots = {}

                querySnapshot.forEach((doc) => {
                    timeSlots[doc.id] = doc.data()
                });

                onSuccess(timeSlots)
            })
            .catch((error) => {
                console.error(error)
                onError(error)
            })
    }

    async allForPatient (type, onSuccess, onError) {
        let currentTime = Math.floor((moment().hour() * 60 + moment().minute()) / 15)

        const uncertifiedOfficesIds = await officeRepository.uncertifiedIds()


        this.collection
            .where('type', '==', type)
            .where('appointmentId', '==', null)
            .where('appointmentInvitationId', '==', null)
            .where('date', '>=', moment().format('YYYY-MM-DD'))
            .where('date', '<=', moment().add(8, 'days').format('YYYY-MM-DD'))
            .get()
            .then((querySnapshot) => {
                let timeSlots = {}

                querySnapshot.forEach((doc) => {
                    let timeslot = doc.data()

                    // Don't add timeslots of uncertified offices
                    if (uncertifiedOfficesIds.includes(timeslot.officeId)) {
                        return
                    }

                    // Only get timeslots for other days or later this day
                    if (timeslot.date >= moment().format('YYYY-MM-DD')) {
                        if (!(timeslot.time <= currentTime && timeslot.date == moment().format('YYYY-MM-DD'))) {
                            timeSlots[doc.id] = timeslot
                        }
                    }
                });

                onSuccess(timeSlots)
            })
            .catch((error) => {
                onError(error)
            })
    }

    allForOffice (officeId, onSuccess, onError) {
        let currentTime = Math.floor((moment().hour() * 60 + moment().minute()) / 15)

        this.collection
            .where('officeId', '==', officeId)
            .where('appointmentId', '==', null)
            .where('appointmentInvitationId', '==', null)
            .where('date', '>=', moment().format('YYYY-MM-DD'))
            .where('date', '<=', moment().add(4, 'days').format('YYYY-MM-DD'))
            .get()
            .then((querySnapshot) => {
                let timeSlots = {}

                querySnapshot.forEach((doc) => {
                    let timeslot = doc.data()

                    // Only get timeslots for other days or later this day
                    if (timeslot.date != moment().format('YYYY-MM-DD') || timeslot.time >= currentTime) {
                        timeSlots[doc.id] = timeslot
                    }
                });

                onSuccess(timeSlots)
            })
            .catch((error) => {
                onError(error)
            })
    }

    bookForPatient (patient, timeslot, onSuccess, onError) {
        const symptoms = timeslot.symptoms
        this.find(timeslot.id, (timeslot) => {
            if (timeslot.appointmentId || timeslot.appointmentInvitationId) {
                onError({message: "Timeslot was booked by someone else"})
                return false
            }

            let batch = db.batch()
            let newAppointment = db.collection('appointments').doc()

            let appointment = {
                id: newAppointment.id,
                appointmentInvitationId: null,
                emergencyType: timeslot.type,
                patientId: patient.id,
                patientFirstName: patient.firstName,
                patientLastName: patient.lastName,
                patientAge: parseInt(patient.age),
                patientEmail: patient.email,
                patientPhone: patient.phone,
                timeSlotId: timeslot.id,
                date: timeslot.date,
                time: timeslot.time,
                doctorId: timeslot.doctorId,
                doctorFirstName: timeslot.doctorFirstName,
                doctorLastName: timeslot.doctorLastName,
                doctorAvatar: timeslot.doctorAvatar,
                isDoctorRegulated: timeslot.isDoctorRegulated,
                officeId: timeslot.officeId,
                officeName: timeslot.officeName,
                officeStreetName: timeslot.officeStreetName,
                officeStreetNumber: timeslot.officeStreetNumber,
                officeZipCode: timeslot.officeZipCode,
                officeLocality: timeslot.officeLocality,
                officeCountry: timeslot.officeCountry,
                officeLocation: timeslot.officeLocation,
                officePhone: timeslot.officePhone,
                symptoms: symptoms
            }

            batch
                .set(newAppointment, appointment)
                .update(db.collection('timeSlots').doc(timeslot.id), {
                    appointmentId: appointment.id
                })
                .commit()
                .then(() => {
                    if (store.state.patient.email === null) {
                        patientRepository
                            .update({
                                id: patient.id,
                                email: helpers.methods.hashPatientInfo(patient.email)
                            }, onSuccess, onError)
                    } else {
                        onSuccess()
                    }
                })
                .catch(onError)

        }, onError)
    }

    find (id, onSuccess, onError) {
        this.collection
            .doc(id)
            .get()
            .then((doc) => {
                if (doc.exists) {
                    onSuccess(doc.data())
                } else {
                    onError({error: "Timeslot was not found"})
                }
            })
            .catch((error) => {
                onError(error)
            })
    }

    // Attach listeners to Firebase
    attachFirebaseListeners () {
        this.collection.onSnapshot((querySnapshot) => {
            for (var key in querySnapshot.docChanges()) {
                var docChange = querySnapshot.docChanges()[key]
                var type = docChange.type
                var doc = docChange.doc

                this.onChanged(type, doc)
            }
        })
    }

    // Detach listeners to Firebase
    detachFirebaseListeners () {
        this.collection.onSnapshot(() => {})
    }

    onChanged (type, doc) {
        this.emit(type, doc.id, doc.exists ? doc.data() : null)
    }
}

export default new TimeSlotRepository()



// WEBPACK FOOTER //
// ./src/data/TimeSlotRepository.js